import React, { useMemo } from "react";
import { connect } from "react-redux";

import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons/faCheckCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";

import { EntityType, EntityTypeSupportingDataMerging } from "@mapmycustomers/shared/types/entity";
import { DuplicableEntity } from "@mapmycustomers/shared/types/entity/Duplicate";
import { Button } from "@mapmycustomers/ui";

import Address from "@app/component/field/Address";
import EntityPriority from "@app/scene/settings/component/MergeDuplicates/enum/EntityPriority";
import {
  getEntityType,
  getSelectedAsPrioritizedEntity,
  getSelectedEntities,
} from "@app/scene/settings/component/MergeDuplicates/store";
import {
  prioritizePrimaryEntity,
  prioritizeSecondaryEntity,
} from "@app/scene/settings/component/MergeDuplicates/store/actions";
import { dataMergingAnalytics } from "@app/scene/settings/settingsAnaytics";
import { RootState } from "@app/store/rootReducer";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { getEntityTypeDisplayName } from "@app/util/ui";

import EntityCell from "./component/EntityCell";
import FieldRow from "./component/FieldRow";
import styles from "./FieldsTable.module.scss";

const getHighLightedPair = (
  priority: EntityPriority,
  primaryValue: string,
  secondaryValue: string
): [boolean, boolean] => {
  const isPrimaryPriority = primaryValue.length && !secondaryValue.length;
  const isSecondaryPriority = !primaryValue.length && secondaryValue.length;
  return [
    !!(
      (priority === EntityPriority.PRIMARY && !isSecondaryPriority) ||
      (priority === EntityPriority.SECONDARY && isPrimaryPriority)
    ),
    !!(
      (priority === EntityPriority.SECONDARY && !isPrimaryPriority) ||
      (priority === EntityPriority.PRIMARY && isSecondaryPriority)
    ),
  ];
};

interface SelectedEntitiesField {
  name: string;
  primaryValue: string;
  secondaryValue: string;
}

interface Props {
  entityType: EntityTypeSupportingDataMerging;
  primary: DuplicableEntity;
  prioritizePrimaryEntity: typeof prioritizePrimaryEntity;
  prioritizeSecondaryEntity: typeof prioritizeSecondaryEntity;
  priority: EntityPriority;
  secondary: DuplicableEntity;
  showedColumns: Set<string>;
}

const FieldsTable: React.FC<Props> = ({
  entityType,
  primary,
  prioritizePrimaryEntity,
  prioritizeSecondaryEntity,
  priority,
  secondary,
  showedColumns,
}) => {
  const intl = useIntl();

  const fields: SelectedEntitiesField[] = useMemo(
    () =>
      getFieldModelByEntityType(entityType)
        .fields.filter(({ name }) => showedColumns.has(name))
        .map((field) => ({
          name: field.displayName,
          primaryValue: field.getFormattedValueFor(primary),
          secondaryValue: field.getFormattedValueFor(secondary),
        })),
    [entityType, primary, secondary, showedColumns]
  );

  const useAsPrimaryText = useMemo(
    () =>
      intl.formatMessage({
        id: "settings.mergeDuplicates.previewMergePane.header.useAsPrimaryText",
        defaultMessage: "Use as primary record",
        description:
          "Settings Merge duplicates - preview/merge pane - fields table - Use as primary record",
      }),
    [intl]
  );

  const selectedAsPrimaryText = useMemo(
    () => (
      <div className={styles.selectedAsPrimary}>
        <div>
          {intl.formatMessage({
            id: "settings.mergeDuplicates.previewMergePane.header.selectedAsPrimaryText",
            defaultMessage: "Selected as primary record",
            description:
              "Settings Merge duplicates - preview/merge pane - fields table - Selected as primary record",
          })}
        </div>
        <FontAwesomeIcon icon={faCheckCircle} />
      </div>
    ),
    [intl]
  );

  const noneText = useMemo(
    () => (
      <div className={styles.noValue}>
        {intl.formatMessage({
          id: "settings.mergeDuplicates.previewMergePane.noValue",
          defaultMessage: "None",
          description: "Settings Merge duplicates - preview/merge pane - fields table - No value",
        })}
      </div>
    ),
    [intl]
  );

  return (
    <div className={styles.container}>
      <FieldRow
        isMainRow
        primaryHighLighted={priority === EntityPriority.PRIMARY}
        primaryText={<EntityCell entity={primary} entityType={entityType} />}
        secondaryHighLighted={priority === EntityPriority.SECONDARY}
        secondaryText={<EntityCell entity={secondary} entityType={entityType} />}
        title={intl.formatMessage({
          id: "settings.mergeDuplicates.previewMergePane.header.primaryColumn",
          defaultMessage: "Potential duplicates",
          description:
            "Settings Merge duplicates - preview/merge pane - fields table - primary columns",
        })}
      />
      <FieldRow
        primaryHighLighted={priority === EntityPriority.PRIMARY}
        primaryText={
          priority === EntityPriority.PRIMARY ? (
            selectedAsPrimaryText
          ) : (
            <Button
              onClick={() => {
                dataMergingAnalytics?.clicked([
                  getEntityTypeDisplayName(intl, entityType as EntityType, {
                    lowercase: false,
                    plural: true,
                  }),
                  "Review & Merge",
                  "Use as primary record",
                ]);
                prioritizePrimaryEntity();
              }}
              type={priority === EntityPriority.NONE ? "primary" : "default"}
            >
              {useAsPrimaryText}
            </Button>
          )
        }
        secondaryHighLighted={priority === EntityPriority.SECONDARY}
        secondaryText={
          priority === EntityPriority.SECONDARY ? (
            selectedAsPrimaryText
          ) : (
            <Button
              onClick={() => prioritizeSecondaryEntity()}
              type={priority === EntityPriority.NONE ? "primary" : "default"}
            >
              {useAsPrimaryText}
            </Button>
          )
        }
        title={intl.formatMessage({
          id: "settings.mergeDuplicates.previewMergePane.header.primaryColumn.choosePrimary",
          defaultMessage: "Choose primary",
          description:
            "Settings Merge duplicates - preview/merge pane - fields table - Choose primary",
        })}
      />
      <FieldRow
        primaryHighLighted={priority === EntityPriority.PRIMARY}
        primaryText={<Address entity={primary} noAddressText={noneText} />}
        secondaryHighLighted={priority === EntityPriority.SECONDARY}
        secondaryText={<Address entity={secondary} noAddressText={noneText} />}
        title={intl.formatMessage({
          id: "settings.mergeDuplicates.previewMergePane.location",
          defaultMessage: "Location",
          description: "Settings Merge duplicates - preview/merge pane - fields table - Location",
        })}
      />

      {fields.map(({ name, primaryValue, secondaryValue }) => {
        const [primaryHighLighted, secondaryHighLighted] = getHighLightedPair(
          priority,
          primaryValue,
          secondaryValue
        );
        return (
          <FieldRow
            key={name}
            primaryHighLighted={primaryHighLighted}
            primaryText={primaryValue.length ? primaryValue : noneText}
            secondaryHighLighted={secondaryHighLighted}
            secondaryText={secondaryValue.length ? secondaryValue : noneText}
            title={name}
          />
        );
      })}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  entityType: getEntityType(state),
  primary: getSelectedEntities(state).primary!,
  priority: getSelectedAsPrioritizedEntity(state),
  secondary: getSelectedEntities(state).secondary!,
});

const mapDispatchToProps = {
  prioritizePrimaryEntity,
  prioritizeSecondaryEntity,
};

export default connect(mapStateToProps, mapDispatchToProps)(FieldsTable);
